import { useContext, useEffect, useState } from "react";
import { Button, Form, Row, Col, Space, Table, Spin, Select } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";

import {
  ePrivileges,
  eResultCode,
  eSERVICEREQUEST,
  eTICKETSTATUS,
  eTICKETSTATUSUNIQUEID,
} from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  TDealer,
  TSpareDependency,
  TTicket,
  TTicketDetail,
} from "../../../types";

import {
  AddEditTicket,
  GetConfigParamDataList,
  GetSpareDepHistory,
  GetSpecificTicket,
} from "../../../utils/api.constant";
import {
  TConfigParam,
  TDropdownOptions,
  TResponseModel,
} from "../../../types/config";
import RowDisplay from "../../../components/data-display";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import { requiredFieldValidator } from "../../../utils/input.validator";
import { CONFIG_GROUPS } from "../../../utils/constants";
import utils from "../../../utils";
import Utils from "../../../utils";
import { UserContext } from "../../../context";

type DrawerProps = {
  ticket?: TTicket;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

export default function ShipmentUpdateForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [dealerDetails, setDealerDetails] = useState<TDealer>();
  const [ticketDetails, setTicketDetails] = useState<TTicket | null>(null);
  const [spareDetails, setSpareDetails] = useState<TSpareDependency[]>([]);
  const [statusOption, setStatusOptions] = useState<TDropdownOptions[]>([]);
  const context: any = useContext(UserContext);

  useEffect(() => {
    const fetchData = async () => {
      await fetchSpecificTicket(props?.ticket?.id || 0);
      await fetchSpareDepHistory();
    };
    fetchData();
  }, [props?.ticket?.dealerId]);

  const fetchSpareDepHistory = async () => {
    try {
      const payload = {
        data: {
          ticketId: props?.ticket?.id,
        },
      };

      const response: TResponseModel = await post(GetSpareDepHistory, payload);
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;

        if (returnCode === eResultCode.SUCCESS) {
          const { spareDependencyList, ...otherData } = data;

          setSpareDetails(spareDependencyList as any);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSpecificTicket = async (id: number) => {
    if (id) {
      try {
        const payload = {
          data: {
            id,
          },
        };
        setIsLoading(true);
        const response: TResponseModel = await post(GetSpecificTicket, payload);
        const { data, dataResponse } = response;
        if (dataResponse.returnCode === eResultCode.SUCCESS) {
          const {
            customerId,
            dealerDetails,
            ticketDetails,
            ticketType,
            issueCatId,
            ...restData
          } = data;
          const { id, dealerName, ...restDetails } = dealerDetails;
          setDealerDetails(dealerDetails);

          setTicketDetails({ ...data, dealerId: id, dealerName });

          // const consolidatedSpareDetails = (
          //   data as TTicket
          // ).ticketDetailList.flatMap(
          //   (ticketDetail: TTicketDetail) =>
          //     ticketDetail.spareDependency?.spareDetails || []
          // );
          await fetchStatusReasonData(data?.currentStatusUniqueId as string);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle on submit event
  const onFinish = async (values: TTicket) => {
    try {
      setIsLoading(true);
      const payload = {
        data: {
          id: props?.ticket?.id,
          ticketDetail: {
            ...values,
          },
        },
      };
      const response = await post(AddEditTicket, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatusReasonData = async (currentStatusUniqueId: string) => {
    try {
      setIsLoading(true);
      const payload = {
        data: {
          groupUniqueId: CONFIG_GROUPS.TICKETSTATUS,
          pageSize: -1,
        },
      };
      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload
      );

      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        updateStatusOptions(data, currentStatusUniqueId);
      }
    } catch (err) {
      console.error("Failed to fetch ticket data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatusOptions = (
    data: TConfigParam[],
    currentStatusUniqueId: string
  ) => {
    let options: TConfigParam[] = [];
    data?.map((option: TConfigParam) => {
      if (
        option.paramUniqueId === eTICKETSTATUSUNIQUEID.SPARE_REQUEST_FORWARD &&
        currentStatusUniqueId !== eTICKETSTATUSUNIQUEID.SPARE_REQUEST_FORWARD &&
        Utils.isUserHasAccess(
          context.user?.privilegeList,
          ePrivileges.SERVICEREQUESTFORWARD
        )
      ) {
        options.push(option);
      } else if (
        option?.paramUniqueId === eTICKETSTATUSUNIQUEID.SPARE_DISPATCHED &&
        Utils.isUserHasAccess(
          context.user?.privilegeList,
          ePrivileges.SPAREDISPATCHED
        )
      ) {
        options.push(option);
      }
    });
    setStatusOptions(utils.mapDropdownOptions(options || []));
  };

  /**
   * Resets the district field and fetches the list of cities based on the selected state
   */
  const contentStyle: React.CSSProperties = {
    padding: 50,
  };

  const content = <div style={contentStyle} />;
  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish} // Pass buttonType to onFinish
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  Spare Status Update
                </h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16} className="formRowHeight">
            {isLoading ? (
              <Col
                span={24}
                style={{
                  justifyContent: "center",
                  display: "flex",
                  marginTop: "2rem",
                  flexDirection: "column",
                }}
              >
                <Spin
                  tip={
                    <span
                      style={{
                        color: "#fff",
                        fontFamily: "Montserrat-Regular",
                      }}
                    >
                      {/* Loading . . . */}
                    </span>
                  }
                  size="large"
                >
                  {content}
                </Spin>
              </Col>
            ) : (
              <>
                <Col span={24}>
                  <RowDisplay
                    heading="Service Request #"
                    content={ticketDetails?.serviceRequestNo || ""}
                  ></RowDisplay>
                </Col>
                <Col span={24}>
                  <RowDisplay
                    heading="Ticket Type"
                    valueRender={() =>
                      (
                        <>
                          {ticketDetails?.ticketType ===
                          eSERVICEREQUEST.OTHER ? (
                            <span>
                              {ticketDetails?.otherTicketTypeList
                                ?.map((item) => item.label)
                                .join(", ")}
                            </span>
                          ) : (
                            <span>{ticketDetails?.ticketTypeName}</span>
                          )}
                        </>
                      ) as any
                    }
                  ></RowDisplay>
                </Col>

                {ticketDetails?.issueCatName ? (
                  <Col span={24}>
                    <RowDisplay
                      heading="Issue"
                      valueRender={() => (
                        <span>{ticketDetails?.issueCatName}</span>
                      )}
                    ></RowDisplay>
                  </Col>
                ) : null}

                {ticketDetails?.subIssueCatOneList?.length ? (
                  <Col span={24}>
                    <RowDisplay
                      heading="Sub Issue Category One"
                      valueRender={() => (
                        <span>
                          {ticketDetails?.subIssueCatOneList
                            ?.map((item) => item.name)
                            .join(", ")}
                        </span>
                      )}
                    ></RowDisplay>
                  </Col>
                ) : null}

                {ticketDetails?.subIssueCatTwoList?.length ? (
                  <Col span={24}>
                    <RowDisplay
                      heading="Sub Issue Category Two"
                      valueRender={() => (
                        <span>
                          {ticketDetails?.subIssueCatTwoList
                            ?.map((item) => item.name)
                            .join(", ")}
                        </span>
                      )}
                    ></RowDisplay>
                  </Col>
                ) : null}

                <Col span={24}>
                  <RowDisplay
                    heading="Dealer Code"
                    content={dealerDetails?.dealerCode || ""}
                  ></RowDisplay>
                </Col>
                <Col span={24}>
                  <RowDisplay
                    heading="Depot Code"
                    content={dealerDetails?.depotCode || ""}
                  ></RowDisplay>
                </Col>
                <Col span={24}>
                  <RowDisplay
                    heading="Dealer Name"
                    content={dealerDetails?.dealerName || ""}
                  ></RowDisplay>
                </Col>
                <Col span={24}>
                  <RowDisplay
                    heading="Dealer Address"
                    content={
                      `${dealerDetails?.address}, ${dealerDetails?.districtName} - ${dealerDetails?.zipcode}, ${dealerDetails?.stateName}` ||
                      ""
                    }
                  ></RowDisplay>
                </Col>
                {/* <Col span={24}>
              <RowDisplay
                heading="Machine Type"
                content={dealerDetails?.machineTypeName || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Model"
                content={dealerDetails?.productName || ""}
              ></RowDisplay>
            </Col>
            <Col span={24}>
              <RowDisplay
                heading="Machine Serial #"
                content={dealerDetails?.machineSerialNo || ""}
              ></RowDisplay>
            </Col> */}

                <Col span={24} style={{ overflowX: "hidden" }}>
                  <Table
                    style={{ width: "100%" }}
                    dataSource={spareDetails}
                    pagination={false}
                    tableLayout="fixed" // Ensures the table does not exceed the container width
                    columns={[
                      {
                        title: "Product Name",
                        dataIndex: "productName",
                        key: "productName",
                        // render: (__: any, spareInfo: TSpareDependency) =>
                        //   spareInfo?.partsInfo?.productName,
                      },
                      {
                        title: "Spare Part #",
                        dataIndex: "sparePartNo",
                        key: "sparePartNo",
                        // render: (__: any, spareInfo: TSpareDependency) =>
                        //   spareInfo?.partsInfo?.partNumber,
                      },
                      {
                        title: "Spare Part Description",
                        dataIndex: "sparePartDescription",
                        key: "sparePartDescription",
                        // render: (__: any, spareInfo: TSpareDependency) => (
                        //   <span>{spareInfo?.partsInfo?.partDescription}</span>
                        // ),
                      },
                    ]}
                  />
                </Col>

                <Col span={24} style={{ marginTop: "1rem" }}>
                  <Form.Item
                    name="statusId"
                    label="Status"
                    rules={[
                      {
                        required: true,
                        message: "Please select status of spares",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select spares status"
                      options={statusOption}
                    />
                  </Form.Item>
                </Col>
                {/* </Col> */}
                <Col span={24}>
                  <Form.Item
                    name="description"
                    label="Remark"
                    rules={[
                      {
                        required: true,
                        validator: requiredFieldValidator("Enter a remark"),
                      },
                    ]}
                  >
                    <TextArea
                      rows={4}
                      placeholder="Enter the remark..."
                      maxLength={500}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </FormContent>
        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              className="btnDimensions"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
            >
              Save
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              onClick={() => {
                props.onCloseDrawer();
                // setButtonType("reject"); // Set button type to "reject"
                // form.submit();
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
